import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { getProLeads, updateBookingStatus, getProProfile, updateOnlineStatus } from "../api";
import { useLanguage } from "../LanguageContext";
import { formatScheduled, formatRelative } from "../utils/dateFormat";
import HamburgerMenu from "../components/HamburgerMenu";
import PaymentModal from "../components/PaymentModal";
import TemplatePicker from "../components/TemplatePicker";
import SecureImage from "../components/SecureImage";

interface Lead {
  bookingId: string;
  createdAt: string;
  serviceType: string;
  address: string;
  scheduledDate: string;
  status: string;
  clientId: string;
  photoUrls?: string[];
  privateNotes?: string;
  jobSize?: string;
  category?: string;
  quoteAmount?: number;
}

const PRO_TOOLS = [
  { id: "services", label: "Services", icon: "🛠️", color: "from-blue-500 to-blue-600", path: "/pro/services" },
  { id: "templates", label: "Templates", icon: "📝", color: "from-purple-500 to-pink-500", path: "/pro/templates" },
  { id: "portfolio", label: "Portfolio", icon: "📸", color: "from-orange-500 to-red-500", path: "/pro/portfolio" },
  { id: "availability", label: "Availability", icon: "📅", color: "from-teal-500 to-cyan-600", path: "/pro/availability" },
  { id: "earnings", label: "Earnings", icon: "💰", color: "from-yellow-500 to-amber-600", path: "/pro/earnings" },
  { id: "service-area", label: "Service Area", icon: "📍", color: "from-emerald-500 to-green-600", path: "/pro/service-area" },
];

export default function ProDashboardPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [proName, setProName] = useState("there");
  const [businessName, setBusinessName] = useState("");
  const [proId, setProId] = useState("");
  const [isOnline, setIsOnline] = useState(false);
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [decliningLead, setDecliningLead] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [payingLead, setPayingLead] = useState<Lead | null>(null);
  const [quotingLead, setQuotingLead] = useState<Lead | null>(null);
  const [quoteInput, setQuoteInput] = useState("");
  const [templateLead, setTemplateLead] = useState<Lead | null>(null);
  const [stats, setStats] = useState({ totalEarnings: 0, activeJobs: 0, completedJobs: 0 });
  const [zipsAreDefault, setZipsAreDefault] = useState(false);
  const [subsAreDefault, setSubsAreDefault] = useState(false);

  const userId = (() => {
    const k = Object.keys(localStorage).find(key => key.includes("idToken"));
    if (!k) return "";
    const t = localStorage.getItem(k);
    if (!t) return "";
    try { return JSON.parse(atob(t.split(".")[1])).sub; } catch { return ""; }
  })();

  useEffect(() => {
    setProId(userId);
    getProProfile().then(p => {
      if (p?.name) setProName(p.name.split(" ")[0]);
      if (p?.businessName) setBusinessName(p.businessName);
      if (p?.isOnline !== undefined) setIsOnline(p.isOnline);
      setZipsAreDefault(p?.serviceZipsAreDefault === true);
      setSubsAreDefault(p?.subcategoriesAreDefault === true);
    }).catch(() => {});

    getProLeads().then(setLeads).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  async function handleToggleOnline() {
    setTogglingOnline(true);
    try {
      await updateOnlineStatus(!isOnline);
      setIsOnline(!isOnline);
    } catch (e) { console.error(e); }
    finally { setTogglingOnline(false); }
  }

  async function handleAccept(lead: Lead, quoteAmount?: number) {
    try {
      await updateBookingStatus(lead.bookingId, lead.createdAt, "confirmed", quoteAmount);
      setLeads(prev => prev.filter(l => l.bookingId !== lead.bookingId));
    } catch (e) { console.error(e); }
  }

  async function handleDecline(lead: Lead, reason: string = "") {
    try {
      await updateBookingStatus(lead.bookingId, lead.createdAt, "declined");
      setLeads(prev => prev.filter(l => l.bookingId !== lead.bookingId));
      setDecliningLead(null); setDeclineReason("");
    } catch (e) { console.error(e); }
  }

  async function handleSignOut() { await signOut(); window.location.href = "/"; }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center gap-3">
          <HamburgerMenu role="pro" />
          <div className="flex-1 flex items-center gap-2">
            <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="pdLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
                <linearGradient id="pdLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
                <linearGradient id="pdBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              </defs>
              <rect width="52" height="52" rx="14" fill="url(#pdBg)"/>
              <g transform="translate(4,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/><circle cx="11" cy="10" r="5" fill="url(#pdLg1)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
              <g transform="translate(25,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/><circle cx="11" cy="10" r="5" fill="url(#pdLg2)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
            </svg>
            <span className="font-display text-lg font-bold text-navy">Mouna</span>
          </div>
          <button onClick={() => navigate("/pro/" + userId)}
            className="w-10 h-10 rounded-full bg-blue-pale flex items-center justify-center text-mouna-blue font-bold text-sm">
            {(businessName || proName).charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      {/* Hero - Pro status & welcome */}
      <div className="bg-gradient-to-br from-mouna-blue via-blue-600 to-navy px-4 pt-8 pb-12 text-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs text-blue-200 font-bold uppercase tracking-wider mb-2">Professional Dashboard</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-2">Hey {proName} 👋</h1>
          <p className="text-blue-100 text-sm mb-5">{businessName || "Manage your business in one place"}</p>

          {/* Online status toggle */}
          <button onClick={handleToggleOnline} disabled={togglingOnline}
            className={"w-full sm:w-auto flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all " +
              (isOnline ? "bg-white text-mouna-blue" : "bg-white/20 text-white border border-white/40")}>
            <div className={"w-2 h-2 rounded-full " + (isOnline ? "bg-teal animate-pulse" : "bg-gray-400")}></div>
            {togglingOnline ? "Updating..." : isOnline ? "🟢 Online — Accepting jobs" : "⚫ Offline — Tap to go online"}
          </button>
        </div>
      </div>

      {/* Service area / subcategories nudge banner */}
      {(zipsAreDefault || subsAreDefault) && (
        <div className="px-4 pt-6 max-w-6xl mx-auto w-full">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:flex sm:items-center sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-amber-900 text-sm mb-1">
                📍 Help us match you with the right leads
              </div>
              <div className="text-xs text-amber-800 leading-relaxed">
                {zipsAreDefault && subsAreDefault && (
                  <>We've set your service area and services to defaults. Refine them to get better-matched leads.</>
                )}
                {zipsAreDefault && !subsAreDefault && (
                  <>Your service area is set to all of Houston by default. Pick the ZIP codes you actually serve.</>
                )}
                {!zipsAreDefault && subsAreDefault && (
                  <>Your services are set to defaults. Pick the specific services you offer to get better-matched leads.</>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:mt-0 flex-shrink-0">
              {zipsAreDefault && (
                <button
                  onClick={() => navigate("/pro/service-area")}
                  className="px-4 py-2 rounded-full bg-amber-900 text-white text-xs font-bold whitespace-nowrap hover:bg-amber-800 transition-colors"
                >
                  Update area →
                </button>
              )}
              {subsAreDefault && (
                <button
                  onClick={() => navigate("/pro/edit")}
                  className="px-4 py-2 rounded-full bg-white border border-amber-300 text-amber-900 text-xs font-bold whitespace-nowrap hover:bg-amber-100 transition-colors"
                >
                  Pick services →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Pro Tools Grid - Angi style */}
      <div className="px-4 pt-8 pb-6 max-w-6xl mx-auto w-full">
        <h2 className="font-display text-xl font-bold text-navy mb-4">Quick access</h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {PRO_TOOLS.map(tool => (
            <button key={tool.id} onClick={() => navigate(tool.path)}
              className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-white hover:shadow-lg transition-all">
              <div className={"w-14 h-14 rounded-2xl bg-gradient-to-br " + tool.color + " flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform"}>
                {tool.icon}
              </div>
              <span className="text-xs font-bold text-navy text-center leading-tight">{tool.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Active Leads */}
      <div className="px-4 pb-8 max-w-6xl mx-auto w-full">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold text-navy">📬 New Leads</h2>
            <p className="text-xs text-gray-500 mt-0.5">{leads.length} {leads.length === 1 ? "request" : "requests"} pending</p>
          </div>
          {leads.length > 0 && <span className="bg-yellow-50 text-yellow-700 text-xs font-bold rounded-full px-3 py-1">{leads.length} new</span>}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2].map(i => <div key={i} className="bg-white rounded-2xl p-4 animate-pulse"><div className="h-4 bg-gray-100 rounded w-3/4 mb-2"></div><div className="h-3 bg-gray-100 rounded w-1/2"></div></div>)}
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="text-5xl mb-3">✅</div>
            <h3 className="font-bold text-navy mb-1">All caught up!</h3>
            <p className="text-sm text-gray-500">No pending leads right now. Stay online to receive new requests.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map(lead => (
              <div key={lead.bookingId} className="bg-white rounded-2xl shadow-card overflow-hidden">
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-bold text-orange-700">⚡ New Lead Request</span>
                  <span className="text-xs text-gray-500">{formatRelative(lead.createdAt)}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-mouna-blue to-navy flex items-center justify-center text-white text-xl flex-shrink-0">⚡</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-navy">{lead.serviceType}</div>
                      <div className="text-xs text-gray-500 mt-0.5">📅 {formatScheduled(lead.scheduledDate)}</div>
                      <div className="text-xs text-gray-500">📍 {lead.address}</div>
                      {lead.jobSize && <span className="inline-block mt-2 bg-blue-pale text-mouna-blue text-xs font-bold rounded-full px-2 py-0.5">📦 {lead.jobSize}</span>}
                    </div>
                  </div>
                  {lead.privateNotes && (
                    <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2 text-xs text-yellow-700 mb-3">
                      🔒 {lead.privateNotes}
                    </div>
                  )}
                  {lead.photoUrls && lead.photoUrls.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {lead.photoUrls.map((url, i) => <SecureImage key={i} src={url} alt="" className="w-full aspect-square object-cover rounded-xl"/>)}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => navigate(`/messages?conversation=${lead.clientId}_${userId}&recipient=${lead.clientId}&name=Client`)}
                      className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 flex-shrink-0">💬</button>
                    <button onClick={() => setTemplateLead(lead)}
                      className="w-10 h-10 bg-blue-pale text-mouna-blue rounded-full flex items-center justify-center hover:bg-mouna-blue hover:text-white flex-shrink-0" title="Send Template">📝</button>
                    <button onClick={() => { setQuotingLead(lead); setQuoteInput(""); }}
                      className="flex-1 bg-mouna-blue text-white rounded-full py-2.5 text-sm font-bold hover:bg-blue-700">Accept</button>
                    <button onClick={() => setDecliningLead(lead.bookingId)}
                      className="bg-gray-100 text-gray-600 rounded-full px-4 py-2.5 text-sm font-bold hover:bg-gray-200">Decline</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Templates Picker */}
      {templateLead && <TemplatePicker lead={templateLead} onClose={() => setTemplateLead(null)} />}

      {/* Decline Modal */}
      {decliningLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5">
            <h3 className="font-bold text-navy text-lg mb-3">Decline this lead?</h3>
            <div className="space-y-2 mb-4">
              {["Too far", "Not available", "Out of expertise", "Schedule conflict", "Other"].map(reason => (
                <button key={reason} onClick={() => setDeclineReason(reason)}
                  className={"w-full p-3 rounded-xl text-sm text-left " + (declineReason === reason ? "bg-mouna-blue text-white" : "bg-gray-50")}>
                  {reason}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setDecliningLead(null); setDeclineReason(""); }}
                className="flex-1 bg-gray-100 text-gray-600 rounded-full py-3 text-sm font-bold">Cancel</button>
              <button onClick={() => { const lead = leads.find(l => l.bookingId === decliningLead); if (lead) handleDecline(lead, declineReason); }}
                disabled={!declineReason}
                className="flex-1 bg-red-500 text-white rounded-full py-3 text-sm font-bold disabled:opacity-50">Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Modal — capture the price the pro will charge the client */}
      {quotingLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-navy">Your quote</h2>
              <button onClick={() => { setQuotingLead(null); setQuoteInput(""); }}
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold">
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              How much will you charge for <span className="font-bold text-navy">{quotingLead.serviceType}</span>?
              The client sees this amount when you accept.
            </p>
            <div className="relative mb-5">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="1"
                step="1"
                autoFocus
                value={quoteInput}
                onChange={(e) => setQuoteInput(e.target.value)}
                placeholder="0"
                className="w-full pl-9 pr-4 py-3 text-2xl font-bold rounded-2xl border border-gray-200 focus:border-mouna-blue focus:outline-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setQuotingLead(null); setQuoteInput(""); }}
                className="flex-1 py-3 rounded-full bg-gray-100 text-navy font-bold text-sm">
                Cancel
              </button>
              <button
                disabled={!quoteInput || Number(quoteInput) <= 0}
                onClick={() => {
                  setPayingLead({ ...quotingLead, quoteAmount: Number(quoteInput) });
                  setQuotingLead(null);
                  setQuoteInput("");
                }}
                className="flex-[2] py-3 rounded-full bg-mouna-blue text-white font-bold text-sm disabled:opacity-50">
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {payingLead && (
        <PaymentModal lead={payingLead} category={payingLead.category || payingLead.serviceType || ""}
          onClose={() => setPayingLead(null)}
          onSuccess={async () => { await handleAccept(payingLead, payingLead.quoteAmount); setPayingLead(null); }}/>
      )}

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-2 z-30">
        <div className="flex items-center justify-around max-w-md mx-auto">
          {[
            { icon: "⊞", label: "Dashboard", path: "/", active: true },
            { icon: "💬", label: "Messages", path: "/messages" },
            { icon: "📅", label: "Schedule", path: "/pro/availability" },
            { icon: "💰", label: "Earnings", path: "/pro/earnings" },
            { icon: "👤", label: "Profile", path: "/pro/" + userId },
          ].map(nav => (
            <button key={nav.label} onClick={() => navigate(nav.path)}
              className={"flex flex-col items-center gap-0.5 px-3 py-2 " + (nav.active ? "text-mouna-blue" : "text-gray-400")}>
              <span className="text-xl">{nav.icon}</span>
              <span className="text-xs font-bold">{nav.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
